// Accessible modal dialog (audit FL-01, FL-29, FA-37).
// * rendered in a portal, role="dialog", aria-modal, labelled by its title;
// * Esc and backdrop click close it (both can be disabled, e.g. for a wizard);
// * focus moves into the dialog and returns to the opener on close;
// * the body scrolls, the footer stays visible; on phones it is a full-height sheet.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export default function Modal({
  open = true,
  title,
  onClose,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  initialFocusRef,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  // Latest callbacks in refs: the focus/keyboard effect below must run only when the
  // dialog opens, otherwise every parent re-render (new inline onClose) would steal
  // focus from the field being typed in.
  const onCloseRef = useRef(onClose);
  const closeOnEscRef = useRef(closeOnEsc);
  onCloseRef.current = onClose;
  closeOnEscRef.current = closeOnEsc;

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const target = initialFocusRef?.current || dialogRef.current?.querySelector(FOCUSABLE) || dialogRef.current;
    target?.focus?.();

    const onKey = (e) => {
      if (e.key === 'Escape' && closeOnEscRef.current && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab' && dialogRef.current) {
        const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center overflow-y-auto bg-black/50 sm:p-4"
      onMouseDown={(e) => {
        if (closeOnBackdrop && onClose && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`flex w-full ${SIZES[size] || SIZES.md} max-h-[100dvh] sm:max-h-[calc(100dvh-2rem)] flex-col bg-white shadow-xl rounded-t-xl sm:rounded-xl focus:outline-none`}
      >
        {(title || onClose) && (
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-3 sm:px-6">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Zavřít"
                className="-mr-2 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">{children}</div>
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
